import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import Modal from '../components/Modal';
import MasterSelect from '../components/MasterSelect';
import PriceModeFields from '../components/PriceModeFields';
import SeasonSelector from '../components/SeasonSelector';
import { PaymentModal } from './PaymentsPage';

const UNITS = ['kg', 'ton', 'adet', 'litre', 'çuval', 'dekar'];

function fmt(n) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(n || 0);
}

// Keep quantity / unit_price / total_amount consistent regardless of which the
// farmer typed. priceMode 'unit' → total = qty × unit; 'total' → unit = total / qty.
function recalcPricing(f) {
  const q = parseFloat(f.quantity);
  if (f.priceMode === 'total') {
    const total = parseFloat(f.totalAmount);
    const up = (q > 0 && !isNaN(total)) ? total / q : '';
    return { ...f, unitPrice: up === '' ? '' : String(up) };
  }
  const up = parseFloat(f.unitPrice);
  const total = (q > 0 && !isNaN(up)) ? q * up : '';
  return { ...f, totalAmount: total === '' ? '' : String(total) };
}

const EMPTY = {
  productName: '', quantity: '', unit: 'kg', unitPrice: '', totalAmount: '',
  priceMode: 'unit',
  contactName: '', paymentStatus: 'pending',
  saleDate: new Date().toISOString().split('T')[0], notes: '',
  masterProductId: '', seasonId: '',
};

export default function SalesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [stockLevel, setStockLevel] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [payments, setPayments] = useState([]);
  const [editId, setEditId] = useState(null);

  // A sale can be corrected only while nothing has been collected on it.
  const isEditable = (item) => parseFloat(item.amount_paid || 0) === 0;

  const openEdit = (item) => {
    setEditId(item.id);
    setStockLevel(null);
    setForm({
      productName: item.product_name || '',
      quantity: item.quantity != null ? String(item.quantity) : '',
      unit: item.unit || 'kg',
      unitPrice: item.unit_price != null ? String(item.unit_price) : '',
      totalAmount: item.total_amount != null ? String(item.total_amount) : '',
      priceMode: 'unit',
      contactName: item.contact_name || '',
      paymentStatus: item.payment_status || 'pending',
      saleDate: (item.sale_date || '').slice(0, 10) || new Date().toISOString().split('T')[0],
      notes: item.notes || '',
      masterProductId: item.master_product_id || '',
      seasonId: item.season_id || '',
    });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setStockLevel(null); setEditId(null); setForm(EMPTY); };

  const load = useCallback(() => {
    setLoading(true);
    api.get('/sales').then(r => setItems(r.data.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    return ['quantity', 'unitPrice', 'totalAmount', 'priceMode'].includes(k) ? recalcPricing(next) : next;
  });

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setPayments([]);
      return;
    }
    setExpandedId(id);
    try {
      const r = await api.get(`/payments/sale/${id}`);
      setPayments(r.data.data || []);
    } catch {
      setPayments([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/sales/${editId}`, form);
        toast.success('Satış güncellendi ✓');
      } else {
        await api.post('/sales', form);
        toast.success('Satış kaydedildi ✓');
      }
      closeModal();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const total = items.reduce((s, i) => s + parseFloat(i.total_amount || 0), 0);

  return (
    <div>
      <div className="card-row" style={{ marginBottom: 16 }}>
        <h2 className="section-title" style={{ margin: 0 }}>Satışlar</h2>
        <span style={{ fontWeight: 800, color: '#27ae60', fontSize: '1.05rem' }}>{fmt(total)}</span>
      </div>

      <div className="card">
        {loading ? <div className="empty-state"><p>Yükleniyor...</p></div>
          : items.length === 0 ? (
            <div className="empty-state">
              <div className="icon">💰</div>
              <p>Henüz satış kaydı yok</p>
            </div>
          ) : items.map(item => (
            <div key={item.id} className="txn-item">
              <div className="txn-main" style={{ cursor: 'pointer' }} onClick={() => toggleExpand(item.id)}>
                <div className="txn-name">{item.product_name}</div>
                <div className="txn-meta">
                  {new Date(item.sale_date).toLocaleDateString('tr-TR')} · {item.quantity} {item.unit}
                  {item.contact_name && ` · ${item.contact_name}`}
                </div>
                <div className="txn-meta" style={{ marginTop: 2 }}>
                  {item.quantity} {item.unit} × {fmt(item.unit_price)} = <b>{fmt(item.total_amount)}</b>
                </div>
                <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span className={`badge badge-${item.payment_status}`}>
                    {item.payment_status === 'paid' ? 'Ödendi' : item.payment_status === 'pending' ? 'Bekliyor' : 'Kısmi'}
                  </span>
                  {item.payment_count > 1 && (
                    <span style={{ fontSize: '0.7rem', color: '#888', fontWeight: 600 }}>
                      {item.payment_count} taksit
                    </span>
                  )}
                  <span style={{ fontSize: '0.7rem', color: '#aaa' }}>{item.receipt_number}</span>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                <span className="txn-amount income">{fmt(item.total_amount)}</span>
                {item.payment_status !== 'paid' && (
                  <button onClick={() => setPayTarget(item)}
                    style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: 8,
                      padding: '3px 8px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
                    💰 Tahsil Et
                  </button>
                )}
                {isEditable(item) && (
                  <button onClick={() => openEdit(item)}
                    style={{ background: '#f0f0f0', color: '#444', border: 'none', borderRadius: 8,
                      padding: '3px 8px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
                    ✏️ Düzenle
                  </button>
                )}
              </div>
              {expandedId === item.id && (
                <div style={{ width: '100%', borderTop: '1px solid #eee', marginTop: 8, paddingTop: 8 }}>
                  {payments.length === 0 ? (
                    <div style={{ fontSize: '0.8rem', color: '#999' }}>Ödeme kaydı yok</div>
                  ) : (
                    <>
                      {payments.map(p => (
                        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', padding: '3px 0', borderBottom: '1px solid #f5f5f5' }}>
                          <span style={{ color: '#555' }}>
                            {new Date(p.payment_date).toLocaleDateString('tr-TR')}
                            {p.method && ` · ${p.method}`}
                          </span>
                          <span style={{ fontWeight: 700, color: '#27ae60' }}>{fmt(p.amount)}</span>
                        </div>
                      ))}
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', fontWeight: 700, marginTop: 4, paddingTop: 4, borderTop: '1px solid #ddd' }}>
                        <span>Kalan</span>
                        <span style={{ color: '#e74c3c' }}>
                          {fmt(parseFloat(item.total_amount) - payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0))}
                        </span>
                      </div>
                    </>
                  )}
                  {item.payment_status !== 'paid' && (
                    <button onClick={() => setPayTarget(item)}
                      style={{ marginTop: 6, background: '#27ae60', color: '#fff', border: 'none', borderRadius: 8,
                        padding: '4px 10px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', width: '100%' }}>
                      Ödeme Ekle
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
      </div>

      <button className="btn-fab" onClick={() => { setForm(EMPTY); setEditId(null); setStockLevel(null); setShowModal(true); }}>+</button>

      {payTarget && (
        <PaymentModal
          entityType="sale" entityId={payTarget.id}
          entityName={payTarget.product_name} totalAmount={payTarget.total_amount}
          direction="in" onClose={() => setPayTarget(null)} onSaved={() => { load(); if (expandedId === payTarget.id) toggleExpand(payTarget.id).then(() => toggleExpand(payTarget.id)); }}
        />
      )}

      {showModal && (
        <Modal title={editId ? 'Satışı Düzenle' : 'Yeni Satış Ekle'} onClose={closeModal}>
          <form onSubmit={handleSubmit}>
            <MasterSelect
              type="product"
              label="Ürün Adı"
              value={form.productName}
              onChange={v => { set('productName', v); if (!v) setStockLevel(null); }}
              onSelect={async (p) => {
                set('unit', p.unit || 'kg');
                set('masterProductId', p.id);
                try {
                  const r = await api.get(`/farm/stock?productName=${encodeURIComponent(p.name)}`);
                  const s = r.data.data?.[0];
                  setStockLevel(s ? parseFloat(s.quantity) : 0);
                } catch { setStockLevel(null); }
              }}
              placeholder="Örn: Buğday, Domates..."
              required
              priorityCategory="crop"
              usageType="sell"
            />
            {stockLevel !== null && (
              <div style={{ background: '#f0f7ee', borderRadius: 8, padding: '6px 10px', marginBottom: 8, fontSize: '0.82rem' }}>
                Mevcut stok: <b style={{ color: stockLevel > 0 ? '#2d5a27' : '#e74c3c' }}>
                  {new Intl.NumberFormat('tr-TR').format(stockLevel)} {form.unit}
                </b>
              </div>
            )}
            <MasterSelect
              type="contact"
              filter={{ type: 'buyer' }}
              label="Alıcı"
              value={form.contactName}
              onChange={v => set('contactName', v)}
              placeholder="Alıcı seçin veya yazın..."
            />
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: 2 }}>🌾 Sezon</label>
            <SeasonSelector value={form.seasonId} onChange={v => set('seasonId', v)} />
            <div className="form-row">
              <div className="form-group">
                <label>Miktar *</label>
                <input type="number" step="0.01" value={form.quantity}
                  onChange={e => set('quantity', e.target.value)} placeholder="0" required />
              </div>
              <div className="form-group">
                <label>Birim</label>
                <select value={form.unit} onChange={e => set('unit', e.target.value)}>
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            {!editId && stockLevel !== null && form.quantity && parseFloat(form.quantity) > stockLevel && (
              <div style={{ color: '#e74c3c', fontSize: '0.78rem', fontWeight: 700, marginBottom: 4 }}>
                ⚠️ Girilen miktar mevcut stoktan fazla!
              </div>
            )}
            <PriceModeFields
              mode={form.priceMode} unit={form.unit}
              quantity={form.quantity} unitPrice={form.unitPrice} totalAmount={form.totalAmount}
              onMode={m => set('priceMode', m)}
              onUnitPrice={v => set('unitPrice', v)}
              onTotal={v => set('totalAmount', v)}
            />
            <div className="form-row">
              <div className="form-group">
                <label>Tarih</label>
                <input type="date" value={form.saleDate} onChange={e => set('saleDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Ödeme</label>
                <select value={form.paymentStatus} onChange={e => set('paymentStatus', e.target.value)}>
                  <option value="paid">✅ Ödendi</option>
                  <option value="pending">⏳ Bekliyor</option>
                  <option value="partial">🔶 Kısmi</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Notlar</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }}
                onClick={closeModal}>İptal</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={saving}>
                {saving ? 'Kaydediliyor...' : '💾 Kaydet'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
